import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "rplc_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeConsent(value) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch {}
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    const value = { necessary: true, analytics: true, marketing: true, ts: Date.now() };
    writeConsent(value);
    setOpen(false);
  };
  const rejectAll = () => {
    const value = { necessary: true, analytics: false, marketing: false, ts: Date.now() };
    writeConsent(value);
    setOpen(false);
  };
  const savePrefs = () => {
    writeConsent({ ...prefs, ts: Date.now() });
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          data-testid="cookie-consent"
          className="fixed z-[90] left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6 lg:left-auto lg:right-8 lg:bottom-8 lg:max-w-[520px]"
        >
          <div className="relative bg-[#0A0A0A] border border-[#222] shadow-2xl">
            <button
              onClick={rejectAll}
              data-testid="cookie-close"
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 border border-[#222] flex items-center justify-center hover:border-[#F4F1EB] transition-colors"
            >
              <X size={12} strokeWidth={1.25} />
            </button>

            <div className="p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2 h-2 bg-[#C84C35]" />
                <span className="chapter-num">Cookies & privacy</span>
              </div>

              <h3 className="font-serif text-2xl lg:text-3xl leading-tight text-[#F4F1EB]">
                Data, respectfully <span className="italic-accent">handled.</span>
              </h3>

              <p className="mt-4 text-sm text-[#A19D94] leading-relaxed">
                We use strictly necessary cookies to run the site, and — with your consent — analytics to understand what our readers find useful. Your choice is honoured across UAE, India and EU visits, and can be changed at any time.
              </p>

              {!details && (
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="cookie-accept" onClick={acceptAll} className="btn-primary" style={{ padding: "0.8rem 1.2rem" }}>
                    Accept all
                  </button>
                  <button data-testid="cookie-reject" onClick={rejectAll} className="btn-ghost" style={{ padding: "0.8rem 1.2rem" }}>
                    Reject non-essential
                  </button>
                  <button
                    data-testid="cookie-manage"
                    onClick={() => setDetails(true)}
                    className="chapter-num text-[#A19D94] hover:text-[#F4F1EB] px-1 self-center link-hover"
                  >
                    Manage preferences
                  </button>
                </div>
              )}

              {details && (
                <div className="mt-6 space-y-4">
                  <PrefRow
                    id="necessary"
                    title="Strictly necessary"
                    desc="Session, security, language preference. Always on."
                    checked={true}
                    disabled
                  />
                  <PrefRow
                    id="analytics"
                    title="Analytics"
                    desc="PostHog page & session analytics — aggregate use only."
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  />
                  <PrefRow
                    id="marketing"
                    title="Marketing"
                    desc="Content personalisation and referrer tracking. Off by default."
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  />
                  <div className="pt-2 flex flex-wrap gap-3">
                    <button data-testid="cookie-save" onClick={savePrefs} className="btn-primary" style={{ padding: "0.8rem 1.2rem" }}>
                      Save preferences
                    </button>
                    <button onClick={() => setDetails(false)} className="chapter-num text-[#A19D94] hover:text-[#F4F1EB] px-1 self-center link-hover">
                      Back
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-[#141414] text-[10px] text-[#66635C] leading-relaxed">
                No lawyer–client relationship is created by using this site. UAE PDPL, India DPDPA & EU GDPR-aligned. See <a href="/privacy" className="link-hover text-[#A19D94]" data-testid="cookie-privacy-link">privacy policy</a>.
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PrefRow({ id, title, desc, checked, onChange, disabled }) {
  return (
    <label htmlFor={`cookie-pref-${id}`} className={`flex items-start gap-4 pb-3 border-b border-[#141414] ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <button
        id={`cookie-pref-${id}`}
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        data-testid={`cookie-toggle-${id}`}
        className={`relative shrink-0 w-10 h-5 border transition-colors duration-500 ${checked ? "bg-[#C84C35] border-[#C84C35]" : "bg-transparent border-[#333]"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        aria-pressed={checked}
      >
        <span
          className="absolute top-0.5 h-3.5 w-3.5 bg-[#F4F1EB] transition-transform duration-500"
          style={{ transform: `translateX(${checked ? 20 : 2}px)` }}
        />
      </button>
      <div className="flex-1">
        <div className="text-[#F4F1EB] text-sm">{title}</div>
        <div className="text-xs text-[#66635C] leading-relaxed mt-1">{desc}</div>
      </div>
    </label>
  );
}
